import { cn } from '@/lib/utils';

interface LogoProps {
  size?: number;
  className?: string;
  /** Show the outer medallion ring. Default true for marquee use; set false
   *  for tight sidebar/nav contexts where just the chalice reads better. */
  ring?: boolean;
  label?: string;
}

/**
 * The Jam-e Jam mark.
 *
 * Three readings in one glyph:
 *  1. A chalice — the mythic Jām-e Jamshid (scrying cup that reveals all
 *     seven layers of the universe → graph-native intelligence archive).
 *  2. A geometric Persian ج (jim — first letter of "Jam"), with its
 *     defining diacritical dot elevated into a "revealing eye" node
 *     inside the bowl.
 *  3. A sovereign medallion seal, matching the daylight/midnight
 *     archive aesthetic.
 *
 * Structural ink uses `currentColor`, so the mark auto-flips between
 * daylight and midnight themes. The accent dots lock to `--color-accent`.
 */
export function Logo({ size = 32, className, ring = true, label = 'Jam-e Jam' }: LogoProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 64 64"
      width={size}
      height={size}
      fill="none"
      stroke="currentColor"
      role="img"
      aria-label={label}
      className={cn('inline-block shrink-0', className)}
    >
      {ring ? (
        <>
          <circle cx="32" cy="32" r="24" strokeWidth="1.2" opacity="0.9" />
          <circle cx="32" cy="32" r="21.5" strokeWidth="0.6" opacity="0.35" />
        </>
      ) : null}

      <path
        d="M 18 21 L 18 29 A 14 14 0 0 0 46 29 L 46 21"
        strokeWidth="3"
        strokeLinecap="round"
      />
      <path d="M 32 36 L 32 43" strokeWidth="3" strokeLinecap="round" />
      <path d="M 25 45 L 39 45" strokeWidth="3" strokeLinecap="round" />

      <circle cx="18" cy="21" r="1.6" fill="var(--color-accent)" stroke="none" />
      <circle cx="46" cy="21" r="1.6" fill="var(--color-accent)" stroke="none" />
      <circle cx="32" cy="30" r="3" fill="var(--color-accent)" stroke="none" />
    </svg>
  );
}
